import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CalculatorIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';

function formatCurrency(value) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

function formatNumber(value, digits) {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(value) ? value : 0);
}

function parseNumericField(raw, fallbackWhenEmpty) {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '' || trimmed === '.' || trimmed === '-') {
    return fallbackWhenEmpty;
  }
  const n = parseFloat(trimmed);
  if (Number.isNaN(n)) return fallbackWhenEmpty;
  return n;
}

function CalculatorInput({ id, label, value, onChange, hint, className }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
      />
      {hint && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

const INPUT_CLS =
  'w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition';

function Result({ label, children, sub, variant = 'blue' }) {
  return (
    <div
      className={`rounded-xl border p-4 min-w-0 ${
        variant === 'teal'
          ? 'border-teal-200 bg-teal-50/90'
          : 'border-blue-100 bg-blue-50/80'
      }`}
    >
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="mt-2 text-xl sm:text-2xl font-bold text-gray-900 break-words tabular-nums">{children}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function SectionShell({ title, subtitle, children, headerTint = 'blue' }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden">
      <div
        className={`px-6 py-5 border-b border-gray-100 ${
          headerTint === 'teal'
            ? 'bg-gradient-to-r from-teal-50 via-white to-cyan-50/50'
            : 'bg-gradient-to-r from-blue-50 via-white to-indigo-50/60'
        }`}
      >
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{subtitle}</p>}
      </div>
      <div className="p-6 lg:p-8">{children}</div>
    </div>
  );
}

const SPREADSHEET_MAILTO = `mailto:info@smileconsent.co.uk?${new URLSearchParams({
  subject: 'NHS calculator spreadsheet request',
  body:
    'Hello SmileConsent,\n\nPlease send me the NHS dental contract calculator spreadsheet (all three calculators).\n\nPractice / organisation name:\n\nBest regards,',
}).toString()}`;

export default function NhsDentalContractCalculators() {
  usePageMeta(
    'NHS dental contract calculators | Practice & associate modelling | SmileConsent',
    'Free calculators for NHS dental practices: unscheduled care, mandated urgent care targets, and associate care packages. Educational tools from SmileConsent.',
    'https://smileconsent.co.uk/nhs-dental-contract-calculators'
  );

  const [unscheduled, setUnscheduled] = useState({
    practiceRate: '30',
    associateRate: '12.5',
    careValue: '60',
    appointmentValue: '15',
  });

  const [urgent, setUrgent] = useState({
    contractValue: '100000',
    annualUdaTarget: '3000',
    urgentCotValue: '75',
  });

  const [carePkg, setCarePkg] = useState({
    practiceUdaRate: '34',
    associateUdaRate: '12.6',
    carePackageValue: '272',
  });

  const unscheduledResults = useMemo(() => {
    const practiceRate = Math.max(0, parseNumericField(unscheduled.practiceRate, 0));
    const associateRate = Math.max(0, parseNumericField(unscheduled.associateRate, 0));
    const careValue = Math.max(0, parseNumericField(unscheduled.careValue, 0));
    const appointmentValue = Math.max(0, parseNumericField(unscheduled.appointmentValue, 0));
    const totalValue = careValue > 0 ? careValue + appointmentValue : appointmentValue;
    const performerUda = practiceRate > 0 ? totalValue / practiceRate : 0;
    const associatePay = performerUda * associateRate;
    const worked =
      `Practice rate: ${formatCurrency(practiceRate)} per UDA. ` +
      `Associate rate: ${formatCurrency(associateRate)} per UDA. ` +
      `Unscheduled care value: ${formatCurrency(careValue)}. ` +
      `Appointment value: ${formatCurrency(appointmentValue)}. ` +
      `Performer UDAs: ${formatNumber(performerUda, 2)}. ` +
      `Associate pay: ${formatCurrency(associatePay)}.`;
    return { totalValue, performerUda, associatePay, worked };
  }, [unscheduled]);

  const urgentResults = useMemo(() => {
    const contractValue = Math.max(0, parseNumericField(urgent.contractValue, 0));
    const annualUdaTarget = Math.max(0, parseNumericField(urgent.annualUdaTarget, 0));
    const urgentCotValue = Math.max(0, parseNumericField(urgent.urgentCotValue, 0));
    const nominalUdaValue = annualUdaTarget > 0 ? contractValue / annualUdaTarget : 0;
    const urgentCotTarget = (contractValue / 10000) * 11;
    const roundedUrgentCotTarget = Math.ceil(urgentCotTarget || 0);
    const urgentCareValue = urgentCotTarget * urgentCotValue;
    const urgentUdasDischarged = nominalUdaValue > 0 ? urgentCareValue / nominalUdaValue : 0;
    const routineUdasRemaining = annualUdaTarget - urgentUdasDischarged;
    const routineFundingRemaining = contractValue - urgentCareValue;
    const urgentShareOfContract = contractValue > 0 ? (urgentCareValue / contractValue) * 100 : 0;
    return {
      nominalUdaValue,
      urgentCotTarget,
      roundedUrgentCotTarget,
      urgentCareValue,
      urgentUdasDischarged,
      routineUdasRemaining,
      routineFundingRemaining,
      urgentShareOfContract,
    };
  }, [urgent]);

  const careResults = useMemo(() => {
    const practiceUdaRate = Math.max(0, parseNumericField(carePkg.practiceUdaRate, 0));
    const associateUdaRate = Math.max(0, parseNumericField(carePkg.associateUdaRate, 0));
    const carePackageValue = Math.max(0, parseNumericField(carePkg.carePackageValue, 0));
    const udasCredited = practiceUdaRate > 0 ? carePackageValue / practiceUdaRate : 0;
    const associatePackageValue =
      practiceUdaRate > 0 ? (carePackageValue * associateUdaRate) / practiceUdaRate : 0;
    const worked =
      `Practice rate: ${formatCurrency(practiceUdaRate)} per UDA. ` +
      `Associate rate: ${formatCurrency(associateUdaRate)} per UDA. ` +
      `Care package value: ${formatCurrency(carePackageValue)}. ` +
      `Credited UDAs: ${formatNumber(udasCredited, 2)}. ` +
      `Associate package value: ${formatCurrency(associatePackageValue)}.`;
    return { udasCredited, associatePackageValue, worked };
  }, [carePkg]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { name: 'Home', href: '/' },
            { name: 'NHS contract calculators', href: '/nhs-dental-contract-calculators' },
          ]}
        />
      </div>

      {/* Hero — matches SmileConsent marketing: blue–indigo + teal accents */}
      <section className="relative border-b border-gray-200 bg-white overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.45]"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 100% 0%, rgba(37, 99, 235, 0.12), transparent 50%), radial-gradient(ellipse 60% 50% at 0% 100%, rgba(20, 184, 166, 0.1), transparent 45%)',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold shadow-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
                  <DocumentCheckIcon className="h-4 w-4 text-white" aria-hidden />
                </span>
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  SmileConsent resource
                </span>
                <span className="text-gray-400">·</span>
                <span className="flex items-center gap-1 text-gray-700">
                  <CalculatorIcon className="h-4 w-4 text-teal-600" aria-hidden />
                  NHS modelling tools
                </span>
              </div>
              <h1 className="mt-6 text-3xl sm:text-4xl lg:text-[2.65rem] font-bold text-gray-900 tracking-tight leading-[1.12]">
                NHS dental contract calculators for{' '}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  practices & performers
                </span>
              </h1>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-2xl">
                Model urgent care, unscheduled care, and associate care packages in your browser—free,
                educational, and independent. For digital consent and audit-ready workflows, teams use{' '}
                <span className="font-semibold text-gray-800">SmileConsent</span>.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-blue-500/25 hover:from-blue-700 hover:to-indigo-700 transition-all"
                >
                  Start free trial
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </Link>
                <Link
                  to="/dental-compliance-software"
                  className="inline-flex items-center justify-center rounded-lg border-2 border-gray-200 bg-white px-6 py-3.5 font-semibold text-gray-800 hover:border-blue-200 hover:bg-blue-50/50 transition-colors"
                >
                  Compliance software
                </Link>
              </div>
              <div className="mt-10 grid sm:grid-cols-3 gap-4">
                {[
                  ['Contract-linked', 'Urgent care targets', 'Tied to annual contract value'],
                  ['Three calculators', 'Live figures', 'Updates as you change inputs'],
                  ['Any device', 'Share with your team', 'Works on phone or desktop'],
                ].map(([k, t, s]) => (
                  <div
                    key={k}
                    className="rounded-xl border border-gray-100 bg-white/90 p-4 shadow-sm"
                  >
                    <div className="text-xs font-medium text-blue-600">{k}</div>
                    <div className="mt-1.5 text-sm font-bold text-gray-900">{t}</div>
                    <div className="mt-1 text-xs text-gray-500 leading-snug">{s}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-blue-600 via-indigo-600 to-indigo-800 p-6 sm:p-8 text-white shadow-xl">
                <DocumentCheckIcon className="h-10 w-10 text-white/90 mb-3" aria-hidden />
                <h2 className="text-xl font-bold">Consent that fits NHS workflows</h2>
                <p className="mt-2 text-sm text-blue-100 leading-relaxed">
                  Send, track, and evidence patient consent in one place—aligned with how UK practices work, so
                  clinicians spend less time on admin.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center rounded-lg bg-white px-5 py-3 text-sm font-semibold text-indigo-700 hover:bg-blue-50 transition-colors"
                  >
                    Talk to us
                  </Link>
                  <Link
                    to="/dental-consent-forms"
                    className="inline-flex items-center justify-center rounded-lg border border-white/40 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                  >
                    Explore consent forms
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unscheduled */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionShell
            title="Unscheduled care calculator"
            subtitle="Model performer UDAs and associate pay for the unscheduled care value plus appointment value."
            headerTint="teal"
          >
            <div className="grid lg:grid-cols-[minmax(0,320px)_1fr] gap-10">
              <div className="space-y-4">
                {[
                  ['ea-ur-pr', 'Practice £ per UDA', 'practiceRate', unscheduled.practiceRate],
                  ['ea-ur-as', 'Associate £ per UDA', 'associateRate', unscheduled.associateRate],
                  ['ea-ur-cv', 'Unscheduled care value (£)', 'careValue', unscheduled.careValue],
                  ['ea-ur-ap', 'Appointment value (£)', 'appointmentValue', unscheduled.appointmentValue],
                ].map(([id, label, key, val]) => (
                  <CalculatorInput
                    key={id}
                    id={id}
                    label={label}
                    value={val}
                    onChange={(next) =>
                      setUnscheduled((prev) => ({ ...prev, [key]: next }))
                    }
                    className={INPUT_CLS}
                  />
                ))}
              </div>
              <div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Result label="Performer UDAs" variant="teal">
                    {formatNumber(unscheduledResults.performerUda, 2)}
                  </Result>
                  <Result label="Associate pay" variant="blue">
                    {formatCurrency(unscheduledResults.associatePay)}
                  </Result>
                  <Result label="Total value (£)" variant="teal">
                    {formatCurrency(unscheduledResults.totalValue)}
                  </Result>
                </div>
                <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-bold text-gray-900">Worked example</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{unscheduledResults.worked}</p>
                </div>
              </div>
            </div>
          </SectionShell>
        </div>
      </section>

      {/* Key points */}
      <section className="pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-4">
            {[
              [
                'Mandatory urgent care activity',
                'Contracts may require a set level of urgent Band 1 activity as unscheduled care each year.',
              ],
              [
                'Linked to contract value',
                'Annual targets tie to contract value—routine planning and capacity need to reflect the split.',
              ],
              [
                'Care package clarity',
                'Transparent UDA and value splits help practices and associates stay aligned.',
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="inline-block rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-bold px-3 py-1">
                  Key change
                </span>
                <h3 className="mt-3 text-lg font-bold text-gray-900">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Know + urgent */}
      <section className="pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
                <h2 className="text-xl font-bold text-gray-900">What dentists need to know</h2>
              </div>
              <div className="p-6 space-y-4">
                {[
                  [
                    'Urgent targets affect planning',
                    'Ringfenced urgent activity can change the balance between routine and urgent access across the year.',
                  ],
                  [
                    'UDA impact can be modelled',
                    'Simple estimates help teams see nominal UDA value and how urgent activity uses capacity.',
                  ],
                  [
                    'Associate transparency',
                    'Clear care-package maths reduces confusion on credited UDAs and retained practice income.',
                  ],
                ].map(([t, b]) => (
                  <div key={t} className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                    <h3 className="font-semibold text-gray-900">{t}</h3>
                    <p className="mt-1 text-sm text-gray-600 leading-relaxed">{b}</p>
                  </div>
                ))}
                <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
                  <p>
                    Educational content only—not legal or contractual advice. Confirm figures against Compass,
                    commissioner communications, and published guidance.
                  </p>
                </div>
              </div>
            </div>

            <SectionShell
              title="Mandated urgent care calculator"
              subtitle="Estimate targets and UDA impact. Urgent CoT target uses contract value ÷ £10k × 11; adjust inputs to match your scenario."
            >
              <div className="space-y-6">
                <div className="space-y-4">
                  <CalculatorInput
                    id="urgent-contract-value"
                    label="Contract value (£)"
                    value={urgent.contractValue}
                    onChange={(next) => setUrgent((p) => ({ ...p, contractValue: next }))}
                    className={INPUT_CLS}
                  />
                  <CalculatorInput
                    id="urgent-annual-uda"
                    label="Annual UDA target"
                    value={urgent.annualUdaTarget}
                    onChange={(next) => setUrgent((p) => ({ ...p, annualUdaTarget: next }))}
                    className={INPUT_CLS}
                  />
                  <CalculatorInput
                    id="urgent-cot-value"
                    label="Urgent CoT value (£)"
                    value={urgent.urgentCotValue}
                    onChange={(next) => setUrgent((p) => ({ ...p, urgentCotValue: next }))}
                    hint="Default £75 for urgent care conversion."
                    className={INPUT_CLS}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Result label="Nominal UDA value" variant="blue">
                    {formatCurrency(urgentResults.nominalUdaValue)}
                  </Result>
                  <Result
                    label="Urgent CoT target"
                    variant="teal"
                    sub={`Rounded requirement: ${urgentResults.roundedUrgentCotTarget} CoTs`}
                  >
                    {formatNumber(urgentResults.urgentCotTarget, 2)}
                  </Result>
                  <Result label="Urgent care value" variant="blue">
                    {formatCurrency(urgentResults.urgentCareValue)}
                  </Result>
                  <Result label="Urgent care UDAs discharged" variant="teal">
                    {formatNumber(urgentResults.urgentUdasDischarged, 2)}
                  </Result>
                  <Result label="Routine UDAs remaining" variant="blue">
                    {formatNumber(urgentResults.routineUdasRemaining, 2)}
                  </Result>
                  <Result label="Routine funding remaining" variant="teal">
                    {formatCurrency(urgentResults.routineFundingRemaining)}
                  </Result>
                  <Result label="Urgent share of contract" variant="blue">
                    {formatNumber(urgentResults.urgentShareOfContract, 2)}%
                  </Result>
                </div>
              </div>
            </SectionShell>
          </div>
        </div>
      </section>

      {/* Care package */}
      <section className="pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionShell
            title="Associate care package calculator"
            subtitle="Model credited UDAs, associate package value, and implied practice retention."
            headerTint="teal"
          >
            <div className="grid lg:grid-cols-[minmax(0,320px)_1fr] gap-10">
              <div className="space-y-4">
                <CalculatorInput
                  id="care-practice-rate"
                  label="Practice £ per UDA"
                  value={carePkg.practiceUdaRate}
                  onChange={(next) => setCarePkg((p) => ({ ...p, practiceUdaRate: next }))}
                  className={INPUT_CLS}
                />
                <CalculatorInput
                  id="care-associate-rate"
                  label="Associate £ per UDA"
                  value={carePkg.associateUdaRate}
                  onChange={(next) => setCarePkg((p) => ({ ...p, associateUdaRate: next }))}
                  className={INPUT_CLS}
                />
                <CalculatorInput
                  id="care-package-value"
                  label="Care package value (£)"
                  value={carePkg.carePackageValue}
                  onChange={(next) => setCarePkg((p) => ({ ...p, carePackageValue: next }))}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Result label="UDAs credited" variant="teal">
                    {formatNumber(careResults.udasCredited, 2)}
                  </Result>
                  <Result label="Associate value of care package" variant="blue">
                    {formatCurrency(careResults.associatePackageValue)}
                  </Result>
                </div>
                <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-bold text-gray-900">Worked example</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{careResults.worked}</p>
                </div>
              </div>
            </div>
          </SectionShell>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 to-white">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Contract questions & answers</h2>
              <p className="mt-1.5 text-sm text-gray-600">Practical pointers for practices and associates.</p>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-5">
              {[
                [
                  'What is changing in the new contract approach?',
                  'A stronger focus on mandated urgent care within NHS contracts, so part of delivery may be ringfenced for urgent access.',
                ],
                [
                  'How might urgent requirements affect routine delivery?',
                  'If more value goes to urgent activity, routine UDA capacity may shrink—planning and scheduling matter more.',
                ],
                [
                  'Does this change total contract value?',
                  'Often the overall value is similar, but the mix between urgent and routine can shift.',
                ],
                [
                  'Why calculate early?',
                  'Early modelling helps forecast displacement, pressures, and staffing before the year runs away.',
                ],
                [
                  'Why care-package transparency?',
                  'Clear numbers reduce disputes on credited UDAs, associate pay, and practice retention.',
                ],
                [
                  'Should owners and associates both review?',
                  'Yes—owners need the contract view; associates need clarity on packages and urgent-care effects.',
                ],
              ].map(([q, a]) => (
                <div
                  key={q}
                  className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 hover:border-blue-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900">{q}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Spreadsheet + CTA */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-md">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex gap-4 min-w-0 flex-1">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/20">
                  <TableCellsIcon className="h-7 w-7 text-white" aria-hidden />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    Excel version of all three calculators
                  </h2>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed max-w-xl">
                    Prefer to model in a spreadsheet? Email us and we&apos;ll send the workbook with the same
                    logic as this page—no charge, from the SmileConsent team.
                  </p>
                  <p className="mt-3 text-xs text-gray-500">
                    Opens your email app to{' '}
                    <span className="font-medium text-gray-700">info@smileconsent.co.uk</span>
                  </p>
                </div>
              </div>
              <a
                href={SPREADSHEET_MAILTO}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-blue-500/25 hover:from-blue-700 hover:to-indigo-700 transition-all shrink-0"
              >
                <EnvelopeIcon className="h-5 w-5" aria-hidden />
                Request spreadsheet
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-600 via-indigo-600 to-indigo-800 p-8 sm:p-10 text-white shadow-xl">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-blue-200">SmileConsent</p>
                <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold leading-tight">
                  Digital consent built for UK dental teams
                </h2>
                <p className="mt-3 text-blue-100 max-w-xl leading-relaxed">
                  Send, track, and evidence consent in one workflow—so you stay focused on patients, not
                  paperwork.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-3.5 font-bold text-indigo-700 hover:bg-blue-50 transition-colors"
                >
                  Start free trial
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-lg border-2 border-white/70 px-6 py-3.5 font-bold text-white hover:bg-white/10 transition-colors"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
